let globalSheets: CSSStyleSheet[] | undefined;

/**
 * Clones all global <style> and <link> styles from the main document into constructable style sheets.
 * Caches the result after first call.
 */
export function getGlobalStyleSheets(): readonly CSSStyleSheet[] {
  if (globalSheets) return globalSheets;

  // Check if document.styleSheets exists and is iterable
  if (!document.styleSheets || typeof document.styleSheets.length !== 'number') {
    globalSheets = [];
    return globalSheets;
  }

  globalSheets = Array.from(document.styleSheets)
    .map(sheet => {
      try {
        // Some styleSheets may not have cssRules (cross-origin, JSDOM, etc.)
        if (!sheet.cssRules) return null;
        const cssRules = Array.from(sheet.cssRules)
          .map(rule => rule.cssText)
          .join(' ');
        const newSheet = new CSSStyleSheet();
        newSheet.replaceSync(cssRules);
        return newSheet;
      } catch (err) {
        console.warn('[getGlobalStyleSheets] Failed to clone stylesheet:', sheet, err);
        return null;
      }
    })
    .filter((s): s is CSSStyleSheet => s !== null);

  return globalSheets;
}

/**
 * Applies all global styles to the given ShadowRoot via adoptedStyleSheets.
 * Defensive: Only applies if adoptedStyleSheets is supported.
 */
export function addGlobalStylesToShadowRoot(shadowRoot: ShadowRoot | null): void {
  if (!shadowRoot) return;

  const sheets = getGlobalStyleSheets();
  // Defensive: Check if adoptedStyleSheets is supported
  if (Array.isArray(shadowRoot.adoptedStyleSheets) && sheets.length) {
    shadowRoot.adoptedStyleSheets.push(...sheets);
  }
}
